import axios, { AxiosResponse } from 'axios';
import { HubConnectionBuilder } from '@microsoft/signalr';
import agent, { accessToken } from './agent';
import {
  mapGeneratedCoursePreview,
  mapSessionState,
  mapSignalRStateToGenerationPhase,
  mapSignalRStateToGenerationStatus,
  normalizeNumericId,
  normalizeSignalRMessage,
  normalizeSignalRRole,
  readNumericIdValue,
  readSignalRProgressUpdate,
  resolveGeneratedCourseStatus,
  resolveStickyGenerationStatus,
} from './aiCourseCreatorSessionMappers';

export { mapSessionState };

export type AICourseCreatorMessageRole = 'user' | 'assistant';

export type AICourseCreatorGeneratedPageStatus = 'queued' | 'generating' | 'created' | 'thumbnail-ready' | 'failed';

export type AICourseCreatorGenerationStatus = 'idle' | 'collecting-context' | 'generating-course' | 'complete' | 'failed';

export type AICourseCreatorGenerationPhase = 'outline' | 'structure' | 'content' | 'assembly' | 'complete';

export interface IAICourseCreatorMessage {
  id: string;
  role: AICourseCreatorMessageRole;
  content: string;
}

export interface IAICourseCreatorGeneratedPagePreview {
  id: string;
  coursePageId?: number;
  sortIndex: number;
  depth: number;
  title: string;
  description?: string;
  status: AICourseCreatorGeneratedPageStatus;
  thumbnailUrl?: string;
}

export interface IAICourseCreatorGeneratedCoursePreview {
  id: string;
  courseId?: number;
  title: string;
  description?: string;
  status: AICourseCreatorGenerationStatus;
  pages: IAICourseCreatorGeneratedPagePreview[];
}

export interface IAICourseCreatorGenerationUpdate {
  status: AICourseCreatorGenerationStatus;
  phase?: AICourseCreatorGenerationPhase;
  course?: IAICourseCreatorGeneratedCoursePreview;
  message?: string;
}

export interface TApiEnvelope<T> {
  data: T | null;
  success: boolean;
  errorMessage: string | null;
}

export interface TEmptyApiEnvelope {
  success: boolean;
  errorMessage: string | null;
}

export interface TPostCourseMessageRequest {
  aiPrompt: string;
  debugMode: boolean;

  /**
   * Existing AI session id. If omitted, the active session is used;
   * if no active session exists, a new one is created.
   */
  ownerAiSessionId?: number | null;

  /**
   * The course the wizard created before the AI steps begin.
   * Strongly recommended. If omitted the backend creates a default course.
   */
  courseID?: number | null;
}

export interface TPostCourseMessageResponse {
  courseId: number;
  aiSessionId: number;
  jobAccepted: boolean;
  statusMessage: string | null;
}

export interface TGetActiveOwnerAISessionResponse {
  aiSessionId: number;
  ownerAiSessionStateTypeKey: string;
}

export interface TGetOwnerAiSessionMessagesRequest {
  aiSessionId: number;
}

export interface TGetOwnerAiSessionMessagesResponse {
  /** 'user' | 'assistant' */
  role: string;
  messageText: string;
  createdAtUtc: string;
}

export interface TContinueInterruptedAiSessionRequest {
  aiSessionId: number;
}

export interface TContinueInterruptedAiSessionResponse {
  aiSessionId: number;
  statusMessage: string | null;
}

export interface TOpenOwnerAiSessionRequest {
  aiSessionId: number;
  ownerAiSessionStateTypeKey: string;
}

export interface TCloseOwnerAiSessionRequest {
  aiSessionId: number;
}

export interface TOwnerAiSessionStateType {
  ownerAiSessionStateTypeID?: number;
  caption?: string;
  reactName?: string;
}

export interface IAICourseCreatorSessionCallbacks {
  onMessages: (messages: IAICourseCreatorMessage[]) => void;
  onSessionState: (stateKey: string) => void;
  onGenerationUpdate: (update: IAICourseCreatorGenerationUpdate) => void;
  onCourseId?: (courseId: number) => void;
  onError: (error: string) => void;
}

export interface IAICourseCreatorSessionSubscription {
  stop: () => void;
  setCourseId: (courseId: number) => void;
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const creatorApiBaseURL =
  process.env.REACT_APP_ENV_API && process.env.REACT_APP_ENV_API.trim() !== '' ? process.env.REACT_APP_ENV_API + '/creatorAPI/' : 'https://niastest02.norskinteraktiv.no/creatorAPI/';

const creatorApi = axios.create({
  baseURL: creatorApiBaseURL,
});

creatorApi.interceptors.request.use(function (config) {
  if (accessToken) {
    config.headers.authorization = `Bearer ${accessToken}`;
  } else if (config.headers && 'authorization' in config.headers) {
    delete config.headers.authorization;
  }
  return config;
});

// Error envelopes can also arrive as HTTP 400/500; surface their errorMessage.
creatorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const envelopeErrorMessage = error?.response?.data?.errorMessage;
    return Promise.reject(typeof envelopeErrorMessage === 'string' && envelopeErrorMessage ? new Error(envelopeErrorMessage) : error);
  },
);

const unwrapEnvelope = <T>(envelope: TApiEnvelope<T>): T | null => {
  if (!envelope || envelope.success !== true) {
    throw new Error(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
  return envelope.data;
};

const unwrapEmptyEnvelope = (envelope: TEmptyApiEnvelope): void => {
  if (!envelope || envelope.success !== true) {
    throw new Error(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
};

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  postEnvelope: <T>(url: string, body?: any) => creatorApi.post<TApiEnvelope<T>>(url, body).then(responseBody).then(unwrapEnvelope),
  postEmptyEnvelope: (url: string, body?: any) => creatorApi.post<TEmptyApiEnvelope>(url, body).then(responseBody).then(unwrapEmptyEnvelope),
};

const requireData = <T>(data: T | null): T => {
  if (data === null || data === undefined) {
    throw new Error(GENERIC_ERROR_MESSAGE);
  }
  return data;
};

const normalizeActiveOwnerAISession = (data: TGetActiveOwnerAISessionResponse | null): TGetActiveOwnerAISessionResponse | null =>
  data ? { ...data, aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId') } : null;

export interface IAICourseCreatorSessionState {
  isInterrupted: boolean;
  isComplete: boolean;
}

const mapMessages = (rawMessages: TGetOwnerAiSessionMessagesResponse[]): IAICourseCreatorMessage[] =>
  rawMessages.map((message, index) => ({
    id: `${message.role}-${message.createdAtUtc}-${index}`,
    role: message.role === 'user' ? 'user' : 'assistant',
    content: message.messageText,
  }));

interface IAICourseCreatorSubscriptionState {
  stopped: boolean;
  currentCourseId: number | null;
  lastStateKey: string | null;
  lastGenerationFingerprint: string;
  latestGenerationStatus: AICourseCreatorGenerationStatus;
  latestGenerationPhase: AICourseCreatorGenerationPhase | null;
  latestGenerationMessage: string | null;
  sessionLooksComplete: boolean;
  deliveredMessages: IAICourseCreatorMessage[];
  liveMessageSequence: number;
  isRefreshingPages: boolean;
  queuedPageRefresh: boolean;
}

const createSubscriptionState = (courseId: number | null): IAICourseCreatorSubscriptionState => ({
  stopped: false,
  currentCourseId: courseId,
  lastStateKey: null,
  lastGenerationFingerprint: '',
  latestGenerationStatus: 'idle',
  latestGenerationPhase: null,
  latestGenerationMessage: null,
  sessionLooksComplete: false,
  deliveredMessages: [],
  liveMessageSequence: 0,
  isRefreshingPages: false,
  queuedPageRefresh: false,
});

const createSessionSubscription = (
  aiSessionId: number,
  courseId: number | null,
  callbacks: IAICourseCreatorSessionCallbacks,
  getMessages: (request: TGetOwnerAiSessionMessagesRequest) => Promise<TGetOwnerAiSessionMessagesResponse[]>,
): IAICourseCreatorSessionSubscription => {
  const state = createSubscriptionState(courseId);

  const connection = new HubConnectionBuilder()
    .withUrl(`${creatorApiBaseURL}hubs/aiSession`, {
      accessTokenFactory: () => accessToken,
    })
    .withAutomaticReconnect()
    .build();

  const emitMessages = (messages: IAICourseCreatorMessage[]) => {
    if (state.stopped) return;
    state.deliveredMessages = messages;
    callbacks.onMessages(messages);
  };

  const emitGenerationUpdate = (update: IAICourseCreatorGenerationUpdate) => {
    if (state.stopped) return;

    const fingerprint = JSON.stringify(update);
    if (fingerprint === state.lastGenerationFingerprint) return;

    state.lastGenerationFingerprint = fingerprint;
    callbacks.onGenerationUpdate(update);
  };

  const applyGenerationStatus = (status: AICourseCreatorGenerationStatus) => {
    state.latestGenerationStatus = resolveStickyGenerationStatus(status, state.sessionLooksComplete);
  };

  const applySessionState = (stateKey: string, message?: string) => {
    const mappedState = mapSessionState(stateKey);
    const wasComplete = state.sessionLooksComplete;
    if (mappedState.isComplete) {
      state.sessionLooksComplete = true;
    }

    applyGenerationStatus(mapSignalRStateToGenerationStatus(stateKey));

    const phase = mapSignalRStateToGenerationPhase(stateKey);
    if (phase) {
      state.latestGenerationPhase = phase;
    }

    const normalizedState = stateKey.trim().toLowerCase();
    const isInternalState = normalizedState === 'starting job!' || normalizedState === 'starting job' || normalizedState === 'idle';
    if (message && !isInternalState && !(wasComplete && mappedState.isComplete)) {
      state.latestGenerationMessage = message;
    }

    if (stateKey !== state.lastStateKey) {
      state.lastStateKey = stateKey;
      callbacks.onSessionState(stateKey);
    }

    emitGenerationUpdate({
      status: state.latestGenerationStatus,
      phase: state.latestGenerationPhase ?? undefined,
      message: state.latestGenerationMessage ?? undefined,
    });
  };

  const reportConnectionError = (message = 'Connection lost. Send a message to retry.') => {
    if (state.stopped) return;
    state.stopped = true;
    callbacks.onError(message);
    void connection.stop().catch(() => undefined);
  };

  const stop = () => {
    if (state.stopped) return;
    state.stopped = true;
    void connection.stop().catch(() => undefined);
  };

  const hydrateMessages = async () => {
    const rawMessages = await getMessages({ aiSessionId });
    emitMessages(mapMessages(rawMessages));
  };

  const refreshGeneratedPages = async () => {
    if (state.stopped || state.currentCourseId === null) return;

    if (state.isRefreshingPages) {
      state.queuedPageRefresh = true;
      return;
    }

    state.isRefreshingPages = true;

    try {
      const course = await agent.Course.getPages(state.currentCourseId);
      if (state.stopped) return;

      const coursePreview = mapGeneratedCoursePreview(course, state.latestGenerationStatus);
      const status = resolveGeneratedCourseStatus(state.sessionLooksComplete, state.latestGenerationStatus, coursePreview.pages.length);

      const update: IAICourseCreatorGenerationUpdate = {
        status,
        phase: state.latestGenerationPhase ?? undefined,
        course: {
          ...coursePreview,
          status,
        },
        message: state.latestGenerationMessage ?? undefined,
      };

      emitGenerationUpdate(update);
    } catch {
      // Page refresh failures are tolerated silently; SignalR owns the connection error surface.
    } finally {
      state.isRefreshingPages = false;
    }

    if (!state.stopped && state.queuedPageRefresh) {
      state.queuedPageRefresh = false;
      void refreshGeneratedPages();
    }
  };

  const updateCourseId = (nextCourseId: number | null) => {
    if (nextCourseId !== null) {
      state.currentCourseId = nextCourseId;
      callbacks.onCourseId?.(nextCourseId);
    }
  };

  const subscribeToHub = async () => {
    await connection.invoke('SubscribeToSessionAsync', aiSessionId);
  };

  const syncSession = async () => {
    await subscribeToHub();
    if (state.stopped) return;
    await hydrateMessages();
    if (state.stopped) return;
    await refreshGeneratedPages();
  };

  const runSubscriptionTask = (task: () => Promise<void>) => {
    void (async () => {
      if (state.stopped) return;

      try {
        await task();
      } catch (error) {
        reportConnectionError(error instanceof Error && error.message ? error.message : undefined);
      }
    })();
  };

  connection.on('OnAiProgressUpdate', (envelope: unknown) => {
    if (state.stopped) return;

    try {
      const update = readSignalRProgressUpdate(envelope);
      if (!update) return;

      updateCourseId(update.courseId);
      if (update.currentState) {
        // Intentionally do not treat isPhaseComplete as terminal completion.
        applySessionState(update.currentState, update.message ?? undefined);
      } else if (update.message) {
        state.latestGenerationMessage = update.message;
        emitGenerationUpdate({
          status: state.latestGenerationStatus === 'idle' ? 'generating-course' : state.latestGenerationStatus,
          phase: state.latestGenerationPhase ?? undefined,
          message: state.latestGenerationMessage,
        });
      }

      void refreshGeneratedPages();
    } catch (error) {
      reportConnectionError(error instanceof Error && error.message ? error.message : undefined);
    }
  });

  connection.on('OnNewChatMessage', (role: unknown, message: unknown) => {
    if (state.stopped) return;

    const nextMessage: IAICourseCreatorMessage = {
      id: `signalr-${aiSessionId}-${state.liveMessageSequence++}`,
      role: normalizeSignalRRole(role),
      content: normalizeSignalRMessage(message),
    };

    emitMessages([...state.deliveredMessages, nextMessage]);
  });

  connection.on('ReceiveSessionStateRestored', (stateString: unknown, restoredCourseId: unknown) => {
    if (state.stopped) return;

    try {
      const stateKey = typeof stateString === 'string' ? stateString.trim() : '';
      if (stateKey) {
        applySessionState(stateKey);
      }

      updateCourseId(readNumericIdValue({ restoredCourseId }, ['restoredCourseId'], 'courseId'));
      void refreshGeneratedPages();
    } catch (error) {
      reportConnectionError(error instanceof Error && error.message ? error.message : undefined);
    }
  });

  connection.onreconnected(() => {
    runSubscriptionTask(syncSession);
  });

  connection.onclose(() => {
    reportConnectionError();
  });

  runSubscriptionTask(async () => {
    await connection.start();
    if (state.stopped) return;
    await syncSession();
  });

  return {
    stop,
    setCourseId: (newCourseId: number) => {
      updateCourseId(newCourseId);
      void refreshGeneratedPages();
    },
  };
};

const CourseCreatorRequests = {
  getActiveOwnerAISession: (): Promise<TGetActiveOwnerAISessionResponse | null> =>
    requests.postEnvelope<TGetActiveOwnerAISessionResponse>('/Ai/GetActiveOwnerAISession').then(normalizeActiveOwnerAISession),
  postCourseMessage: (request: TPostCourseMessageRequest): Promise<TPostCourseMessageResponse> =>
    requests.postEnvelope<TPostCourseMessageResponse>('/Ai/PostCourseMessage', request).then((response) => {
      const data = requireData(response);
      return {
        ...data,
        courseId: normalizeNumericId(data.courseId, 'courseId'),
        aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId'),
      };
    }),
  continueInterruptedAiSession: (request: TContinueInterruptedAiSessionRequest): Promise<TContinueInterruptedAiSessionResponse> =>
    requests.postEnvelope<TContinueInterruptedAiSessionResponse>('/Ai/ContinueInterruptedAiSession', request).then((response) => {
      const data = requireData(response);
      return {
        ...data,
        aiSessionId: normalizeNumericId(data.aiSessionId, 'aiSessionId'),
      };
    }),
  getOwnerAiSessionMessages: (request: TGetOwnerAiSessionMessagesRequest): Promise<TGetOwnerAiSessionMessagesResponse[]> =>
    requests.postEnvelope<TGetOwnerAiSessionMessagesResponse[]>('/Ai/GetOwnerAiSessionMessages', request).then((data) => data ?? []),
  openOwnerAiSession: (request: TOpenOwnerAiSessionRequest): Promise<TGetActiveOwnerAISessionResponse | null> =>
    requests.postEnvelope<TGetActiveOwnerAISessionResponse>('/Ai/OpenOwnerAiSession', request).then(normalizeActiveOwnerAISession),
  closeOwnerAiSession: (request: TCloseOwnerAiSessionRequest): Promise<void> => requests.postEmptyEnvelope('/Ai/CloseOwnerAiSession', request),
  getOwnerAiSessionStateTypes: (): Promise<TOwnerAiSessionStateType[]> => requests.postEnvelope<TOwnerAiSessionStateType[]>('/Ai/GetOwnerAiSessionStateTypes').then((data) => data ?? []),
};

const CourseCreator = {
  ...CourseCreatorRequests,
  subscribeToSession: (aiSessionId: number, courseId: number | null, callbacks: IAICourseCreatorSessionCallbacks): IAICourseCreatorSessionSubscription =>
    createSessionSubscription(aiSessionId, courseId, callbacks, CourseCreatorRequests.getOwnerAiSessionMessages),
  mapSessionState,
};

const aiCourseCreatorAgent = {
  courseCreator: CourseCreator,
};

export default aiCourseCreatorAgent;
